import React, { ReactNode, MouseEvent } from 'react'
import { BaseContentBox, BaseContentBoxProps } from '@pokemon/design.ui.containers.base-content-box'
import { StepIndicator, StepIndicatorProps } from '@pokemon/design.ui.step-indicator'
import './content-box.css'
import { useTranslation } from 'react-i18next'

/**
 * Props to use when initializing a `ContentBox` component.
 */
export interface ContentBoxProps extends BaseContentBoxProps {
  /**
   * a node to be rendered in the special component.
   */
  children?: ReactNode
  /**
   * what to do when someone clicks a modal back button - also causes the back button to render if truthy
   */
  onBack?: (e: MouseEvent) => void
  /**
   * what to do when someone clicks a modal close button - also causes the close button to render if truthy
   */
  onClose?: (e: MouseEvent) => void
  /**
   * a react component to be rendered as the logo for the content box - defaults to Pokémon logo
   */
  Logo?: (() => JSX.Element) | null
  /**
   * whether to include a default meta tag
   */
  includeMeta?: boolean
  /**
   * back button Alt text
   */
  backButtonAltText?: string
  /**
   * close button Alt text
   */
  closeButtonAltText?: string
  /**
   * to display a step indicator we need to pass StepIndicatorProps
   */
  stepIndicatorProps?: StepIndicatorProps
  /**
   * If passing a value of true the size of the box will adjust automatically for medium and small devices
   */
  autoSizeBox?: boolean
}

ContentBox.defaultProps = {
  Logo: () => <img className='logo' src={'https://static.pokemon.com/images/pokemon_logo.png'} alt={'Pokémon'} />,
  includeMeta: true
}

/**
 * Header used in content box.
 */
function Header({ Logo, includeMeta }) {
  return (
    <header>
      {includeMeta && <meta name='viewport' content='width=device-width, initial-scale=1.0' />}
      {Logo && <div className='logo-wrapper'>
        <Logo />
      </div>}
    </header>
  )
}

/**
 * Content box for PTCS apps.
 */
export function ContentBox({ children, onBack, onClose, backButtonAltText, closeButtonAltText, Logo, includeMeta, stepIndicatorProps, autoSizeBox, ...otherProps }: ContentBoxProps) {
  const { t } = useTranslation()

  return (
    <BaseContentBox autoSizeBox={autoSizeBox} {...otherProps}>
      <div className={stepIndicatorProps && onClose && !onBack ? 'top-container-grid' : stepIndicatorProps
        && onBack && !onClose ? 'top-container-grid' : 'top-bar-container'}>
        {onBack ? <button data-testid='back-button' className='content-box-button back-button' onClick={onBack}>
          <img src={'https://static.pokemon.com/images/back_button.svg'} alt={backButtonAltText || t('back_button', 'Back')} />
        </button> : null}
        {stepIndicatorProps ? <div className={stepIndicatorProps && onClose && !onBack || stepIndicatorProps
          && onBack && !onClose ? 'indicator-grid' : ''}><StepIndicator {...stepIndicatorProps} /> </div> : null}
        {onClose ? <button data-testid='close-button' className='content-box-button close-button' onClick={onClose}>
          <img src={'https://static.pokemon.com/images/times_solid.svg'} alt={closeButtonAltText || t('close_button', 'Close')} />
        </button> : null}
      </div>
      <Header Logo={Logo} includeMeta={includeMeta} />
      {children}
    </BaseContentBox>
  )
}
